// Minimal promise-based IndexedDB key-value wrapper (no external deps).
//
// Used as the DURABLE backing store for sessions + outcomes. IndexedDB has a
// far larger quota than localStorage (eliminating the QuotaExceededError that
// silently dropped long conversations) and survives more reliably across
// reloads. It is still subject to iOS ~7-day eviction (a platform limit), so it
// is paired with a localStorage mirror + export/backup, not a substitute for them.
//
// Every call is guarded: in environments without IndexedDB (jsdom tests,
// private mode, old browsers) the functions resolve to null/false and the
// caller transparently falls back to the localStorage mirror.

const DB_NAME = "wingman_db";
const STORE = "kv";
const DB_VERSION = 1;

const _available =
  typeof indexedDB !== "undefined" && indexedDB !== null;

export const isIdbAvailable = () => _available;

let _dbPromise = null;

const openDB = () => {
  if (!_available) return Promise.reject(new Error("indexedDB unavailable"));
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      reject(e);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  // If opening fails, allow a later retry rather than caching the rejection.
  _dbPromise.catch(() => { _dbPromise = null; });
  return _dbPromise;
};

export const idbGet = async (key) => {
  if (!_available) return null;
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result === undefined ? null : req.result);
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    return null;
  }
};

export const idbSet = async (key, value) => {
  if (!_available) return false;
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch (e) {
    return false;
  }
};

export const idbDel = async (key) => {
  if (!_available) return false;
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    return false;
  }
};
